import { createSelector } from "@reduxjs/toolkit";
import { DOC_SOURCE } from "constants/documents";
import { isEmpty } from "lodash";

const EMPTY = [];

export const selectWritingState = (state) => state.writing;

export const selectAllDocumentsByEntityType = (entityType) => (state) =>
  state?.writing?.documents?.[entityType]?.documents ?? [];

export const selectIsLoadingDocumentsByEntityType = (entityType) => (state) =>
  Boolean(state?.writing?.documents?.[entityType]?.isLoading);

export const selectCurrentDocument = createSelector(
  [selectWritingState],
  (writing) => writing?.currentDocument
);

export const selectCurrentContent = createSelector(
  [selectCurrentDocument],
  (currentDocument) => {
    return currentDocument?.content;
  }
);

export const selectDocumentVersionId = createSelector(
  [selectCurrentDocument],
  (currentDocument) => currentDocument.versionId
);

export const selectHasScoringData = createSelector(
  [selectCurrentDocument],
  (currentDocument) => Boolean(currentDocument?.hasScoringData)
);

export const selectIsDocumentSyncing = (state) =>
  state.writing.currentDocument?.isSyncing;

export const selectLinkedOutlineItems = (state) =>
  state?.writing?.currentDocument?.linkedOutlineItems || EMPTY;

export const selectBaseOutlineHeadingLevel = createSelector(
  [selectLinkedOutlineItems],
  (outlineItems) => {
    if (!outlineItems?.length) return Number.MIN_SAFE_INTEGER;

    return Math.min(...outlineItems.map((item) => item.level));
  }
);

export const selectCurrentDocumentTitle = (state) =>
  state?.writing?.currentDocument?.title;

export const selectIsLockedBy = (state) =>
  state?.writing?.currentDocument?.isLockedBy;

export const selectIsAddInDocument = createSelector(
  [selectCurrentDocument],
  (currentDocument) => currentDocument?.source === DOC_SOURCE.ADDIN
);

export const selectIsLockedByAnotherUser = (state) =>
  Boolean(state?.writing?.currentDocument?.isLockedByAnotherUser);

export const selectIsLockedByAppUser = (state) =>
  Boolean(state?.writing?.currentDocument?.isLockedByAppUser);

export const selectHasActiveEditors = createSelector(
  [selectCurrentDocument],
  (currentDocument) => Boolean(currentDocument?.activeEditors?.length)
);

export const selectIsReadonlyDocument = createSelector(
  [selectIsAddInDocument, selectIsLockedByAnotherUser],
  (isAddInDocument, isLockedByOther) => {
    return isAddInDocument || isLockedByOther;
  }
);

export const selectLastUpdatedDocument = createSelector(
  [selectCurrentDocument],
  (currentDocument) => {
    if (isEmpty(currentDocument)) return [];

    const { lastUpdatedAt, lastUpdatedBy } = currentDocument;
    return [lastUpdatedAt, lastUpdatedBy];
  }
);

export const selectCurrentDocumentId = (state) =>
  state?.writing?.currentDocument?.id;

export const selectLastUsedWatchWordList = (state) =>
  state?.writing?.currentDocument?.lastUsedWatchWordList;

export const selectIsReadabilityViewByEntityType = (entityType) => (state) =>
  Boolean(state?.writing?.documents?.[entityType]?.isReadabilityView);

export const selectReadabilityScoresByEntityType = (entityType) => (state) =>
  state?.writing?.documents?.[entityType]?.readabilityScores ?? {};

export const selectReadabilityThresholdsByEntityType =
  (entityType) => (state) =>
    state?.writing?.documents?.[entityType]?.readabilityThresholds ?? null;
